import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Box, Button, Chip, CircularProgress, IconButton, List, ListItemButton, ListItemText, Popover } from "@mui/material";
import { NotebookPen, CalendarDays, ArrowRight, CheckCheck } from "lucide-react";
import { useHistory } from "react-router-dom";
import { notesApi } from "./notesApi";
import useNotesSession from "./useNotesSession";
import "./Notes.scss";
import { formatNoteDate, statusColor, statusLabels } from "./noteDates";

export default function NotesBell() {
  const token = useNotesSession();
  return token ? <SessionBell key={token} token={token} /> : null;
}
function SessionBell({ token }) {
  const api = useMemo(() => notesApi(token), [token]);
  const history = useHistory();
  const [anchor, setAnchor] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let controller;
    let active = true;
    const load = async () => {
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      try {
        const result = await api.notifications(signal);
        if (active && !signal.aborted && localStorage.getItem("token") === token) { setData(result); setError(false); }
      } catch (e) {
        if (active && !signal.aborted) { setError(true); setData(null); }
      }
    };
    load();
    const timer = window.setInterval(load, 60000);
    window.addEventListener("notes-changed", load);
    window.addEventListener("focus", load);
    return () => {
      active = false; controller?.abort(); window.clearInterval(timer);
      window.removeEventListener("notes-changed", load); window.removeEventListener("focus", load);
    };
  }, [api, token, refresh]);
  const open = path => { setAnchor(null); history.push(path); };
  return <>
    <div className="header-action-slot">
      <IconButton className="icon-btn header-action-btn" title="Shënimet dhe afatet" aria-label={`Shënimet dhe afatet${data?.Count ? `, ${data.Count} afate aktive` : ""}`}
        onClick={e => { setAnchor(e.currentTarget); setRefresh(r => r + 1); }}>
        <Badge badgeContent={data?.Count || null} color="error" max={99}><NotebookPen size={20} /></Badge>
      </IconButton>
    </div>
    <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }} PaperProps={{ className: "NOTES__popover", sx: { width: 400, maxWidth: "calc(100vw - 24px)" } }}>
      <div className="NOTES__popover-heading"><span className="NOTES__dialog-icon"><CalendarDays size={21} /></span><div><h2>Shënimet dhe afatet</h2><p>Mbaji afatet në vëmendje.</p></div></div>
      {data && <div className="NOTES__popover-summary"><span><strong>{data.DueTodayCount}</strong> për sot</span><span><strong>{data.OverdueCount}</strong> të vonuara</span></div>}
      {error ? <Alert severity="warning" sx={{ mx: 2 }}>Afatet nuk u ngarkuan. <Button onClick={() => setRefresh(r => r + 1)}>Provo përsëri</Button></Alert>
        : !data ? <Box p={2}><CircularProgress size={22} aria-label="Duke ngarkuar afatet" /></Box>
        : !data.Count ? <div className="NOTES__popover-empty"><CheckCheck size={28} /><strong>Asnjë afat në pritje</strong><p>Nuk ka afate për sot ose të vonuara.</p></div>
        : <List className="NOTES__popover-list" sx={{ maxHeight: 360, overflow: "auto" }}>{data.Items.map(note =>
          <ListItemButton key={note.NoteID} onClick={() => open(`/notes?note=${note.NoteID}`)}>
            <ListItemText primary={note.Title} secondary={formatNoteDate(note.DeadlineDate)} sx={{ overflowWrap: "anywhere" }} />
            <Chip size="small" variant="outlined" label={statusLabels[note.Status]} color={statusColor(note.Status)} />
          </ListItemButton>)}</List>}
      <div className="NOTES__popover-footer"><Button fullWidth endIcon={<ArrowRight size={16} />} onClick={() => open("/notes")}>Hap të gjitha shënimet</Button></div>
    </Popover>
  </>;
}
